import React from 'react';
import {
  Box,
  Button,
  ButtonIcon,
  ButtonText,
  Heading,
  HStack,
  Input,
  InputField,
  Spinner,
  Text,
} from '@gluestack-ui/themed';
import { Download, RefreshCw } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';
import { useGetGradingStatsReport } from '@/common/api/endpoints/reports/reports';
import type { GradingStatsRow } from '@/common/api/models';
import { AdminWidget, DataTable } from '../common';
import type { ColumnDefinition } from '../common/DataColumnConfig';

const getDefaultMonthRange = () => {
  const now = new Date();
  const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
  return {
    fromDate: from.toISOString().slice(0, 10),
    toDate: to.toISOString().slice(0, 10),
  };
};

const buildCsv = (rows: GradingStatsRow[], headers: string[]) => {
  const lines = [
    headers.join(','),
    ...rows.map((row) => [
      row.lecturerName,
      row.month,
      row.total,
      row.withOralExam,
      row.withoutOralExam,
    ].join(',')),
  ];
  return `${lines.join('\n')}\n`;
};

const COLUMNS: Record<string, ColumnDefinition<GradingStatsRow>> = {
  lecturerName: {
    label: 'Lecturer',
    width: 220,
    type: 'string',
    sortable: true,
    defaultSort: 'asc',
  },
  month: {
    label: 'Month',
    width: 100,
    type: 'string',
    sortable: true,
  },
  total: {
    label: 'Total graded',
    width: 110,
    type: 'int',
    sortable: true,
  },
  withOralExam: {
    label: 'With oral exam',
    width: 140,
    type: 'int',
    sortable: true,
  },
  withoutOralExam: {
    label: 'Without oral exam',
    width: 160,
    type: 'int',
    sortable: true,
  },
};

/**
 * GradingStatsPanel
 * ----
 * Admin Insights view showing per-lecturer grading counts by calendar month.
 * Filter bar deliberately matches DataTable's function-bar visual style.
 */
const GradingStatsPanel: React.FC = () => {
  const { t } = useTranslation();
  const defaults = React.useMemo(() => getDefaultMonthRange(), []);

  const [fromDate, setFromDate] = React.useState(defaults.fromDate);
  const [toDate, setToDate] = React.useState(defaults.toDate);
  const [appliedRange, setAppliedRange] = React.useState(defaults);
  const [lecturerFilter, setLecturerFilter] = React.useState('');

  const queryParams = React.useMemo(() => ({
    from: `${appliedRange.fromDate}T00:00:00.000Z`,
    to: `${appliedRange.toDate}T00:00:00.000Z`,
  }), [appliedRange]);

  const { data: response, isLoading } = useGetGradingStatsReport(queryParams, {
    query: {
      select: (res) => res?.data?.data?.data ?? [],
    },
  });

  const rows = React.useMemo(
    () => (response as GradingStatsRow[] | undefined) ?? [],
    [response],
  );
  const filteredRows = React.useMemo(() => {
    const q = lecturerFilter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => row.lecturerName?.toLowerCase().includes(q));
  }, [rows, lecturerFilter]);
  const pagination = React.useMemo(() => ({
    page: 1,
    pageSize: Math.max(filteredRows.length, 1),
    total: filteredRows.length,
  }), [filteredRows.length]);

  const handleApplyRange = () => setAppliedRange({ fromDate, toDate });

  const handleExportCsv = () => {
    if (Platform.OS !== 'web' || filteredRows.length === 0) return;
    const headers = [
      t('admin.gradingStats.columns.lecturer'),
      t('admin.gradingStats.columns.month'),
      t('admin.gradingStats.columns.total'),
      t('admin.gradingStats.columns.withOralExam'),
      t('admin.gradingStats.columns.withoutOralExam'),
    ];
    const blob = new Blob([buildCsv(filteredRows, headers)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `grading-stats-${appliedRange.fromDate}-${appliedRange.toDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminWidget title="" w="100%" h="100%">
      {/* Function bar - identical structure/styling to DataTableFunctionBar container */}
      <Box
        px="$6"
        py="$4"
        mb="$2"
        borderBottomWidth={1}
        borderBottomColor="$borderLight200"
      >
        <HStack
          sx={{ justifyContent: 'space-between' } as React.ComponentProps<typeof HStack>['sx']}
          alignItems="center"
        >
          {/* Left: title */}
          <Heading size="md" color="$primary" mr="$6">
            {t('admin.gradingStats.title')}
          </Heading>

          {/* Middle: date range controls */}
          <HStack space="md" alignItems="center" flex={1}>
            <HStack space="xs" alignItems="center">
              <Text size="sm" color="$textSubtle">{t('admin.gradingStats.filters.from')}</Text>
              <Input size="sm" w={130}>
                <InputField
                  value={fromDate}
                  onChangeText={setFromDate}
                  placeholder="YYYY-MM-DD"
                  testID="grading-stats-from"
                />
              </Input>
            </HStack>

            <HStack space="xs" alignItems="center">
              <Text size="sm" color="$textSubtle">{t('admin.gradingStats.filters.to')}</Text>
              <Input size="sm" w={130}>
                <InputField
                  value={toDate}
                  onChangeText={setToDate}
                  placeholder="YYYY-MM-DD"
                  testID="grading-stats-to"
                />
              </Input>
            </HStack>

            <HStack space="xs" alignItems="center">
              <Text size="sm" color="$textSubtle">{t('admin.gradingStats.filters.lecturer')}</Text>
              <Input size="sm" w={180}>
                <InputField
                  value={lecturerFilter}
                  onChangeText={setLecturerFilter}
                  placeholder={t('admin.gradingStats.filters.lecturerPlaceholder')}
                  testID="grading-stats-lecturer"
                />
              </Input>
            </HStack>

            {!!isLoading && <Spinner size="small" />}
          </HStack>

          {/* Right: action buttons */}
          <HStack space="sm">
            <Button
              size="sm"
              borderRadius="$lg"
              onPress={handleApplyRange}
              testID="grading-stats-apply"
            >
              <ButtonIcon as={RefreshCw} mr="$1.5" />
              <ButtonText>{t('admin.gradingStats.filters.apply')}</ButtonText>
            </Button>

            {Platform.OS === 'web' && (
              <Button
                size="sm"
                action="secondary"
                variant="outline"
                borderRadius="$lg"
                onPress={handleExportCsv}
                isDisabled={filteredRows.length === 0}
                testID="grading-stats-export"
              >
                <ButtonIcon as={Download} mr="$1.5" />
                <ButtonText>{t('admin.gradingStats.exportCsv')}</ButtonText>
              </Button>
            )}
          </HStack>
        </HStack>
      </Box>

      {/* Table - noTitle collapses the DataTable's own header box */}
      <Box flex={1}>
        <DataTable
          dataConfig={{
            objectName: 'reports/grading-stats',
            data: filteredRows,
            pagination,
            disableDataFetching: true,
            isLoading,
          }}
          displayConfig={{
            tableTitle: '',
            noTitle: true,
            columns: COLUMNS,
          }}
          searchFilterConfig={{
            showSearch: false,
            showStateFilter: false,
          }}
          paginationSelectionConfig={{
            enableSelection: false,
            pageSize: filteredRows.length || 25,
          }}
        />
      </Box>
    </AdminWidget>
  );
};

export default GradingStatsPanel;
